/**
 * 🔒 A list row's Marks column (piece 13; decisions 9720, 10070; T3164, T3457):
 * the gold suggestions dot of T3028 and the one warning triangle of a failed
 * check on the record or on any record under it, in one column first after the
 * checkbox. The row carries its marks as words joined by a comma and a space,
 * so the export and a view's conditions read them; the list draws the glyphs in
 * their place. 🔨 Whether a check fails, and how its failure climbs the record
 * tree, is the API's (`company-compliance.ts`, `record-marks.ts`); this module
 * names what the server answered and words each failure's line.
 */
import { fill, t, type StringKey } from '../strings';

/** The marks a row can carry, in the order the column draws them. */
export const LIST_MARKS = ['suggestions', 'missingW9', 'noTaxExemptCertificate'] as const;
export type ListMark = (typeof LIST_MARKS)[number];

const MARK_WORD: Readonly<Record<ListMark, StringKey>> = {
  suggestions: 'listMarks.suggestions',
  missingW9: 'listMarks.missingW9',
  noTaxExemptCertificate: 'listMarks.noTaxExemptCertificate',
};

/** The word a mark reads as in the export, a view's conditions and a built-in view's name. */
export function listMarkWord(mark: ListMark): string {
  return t(MARK_WORD[mark]);
}

/** A row's Marks cell: its marks' words in the column's order, joined by a comma and a space; empty where it carries none. */
export function listMarksText(marks: readonly ListMark[]): string {
  return LIST_MARKS.filter((mark) => marks.includes(mark)).map(listMarkWord).join(', ');
}

/** 🔒 Why a vendor's W-9 is missing: none on file, one rejected (the status bounced back with a reason), or one awaiting approval. */
export const W9_GAPS = ['absent', 'rejected', 'pendingApproval'] as const;
export type W9Gap = (typeof W9_GAPS)[number];

/** 🔒 Why a flagged site fails its certificate check: none in force, or only expired ones (decision 10070). */
export const CERTIFICATE_GAPS = ['none', 'expired'] as const;
export type CertificateGap = (typeof CERTIFICATE_GAPS)[number];

const W9_REASON: Readonly<Record<W9Gap, StringKey>> = {
  absent: 'listMarks.w9.absent',
  rejected: 'listMarks.w9.rejected',
  pendingApproval: 'listMarks.w9.pendingApproval',
};

const CERTIFICATE_REASON: Readonly<Record<CertificateGap, StringKey>> = {
  none: 'listMarks.certificate.none',
  expired: 'listMarks.certificate.expired',
};

/** The sentence a vendor's failed W-9 check reads on its own company's row. */
export function w9GapReason(gap: W9Gap): string {
  return t(W9_REASON[gap]);
}

/** The sentence a flagged site's failed certificate check reads, naming the site. */
export function certificateGapReason(gap: CertificateGap, site: string): string {
  return fill(t(CERTIFICATE_REASON[gap]), { site });
}

/** 🔒 The checks a record can fail today (decision 10070): a vendor company's W-9 and a tax exempt customer site's certificate. */
export const RECORD_CHECKS = ['w9', 'taxExemptCertificate'] as const;
export type RecordCheck = (typeof RECORD_CHECKS)[number];

/** The record kinds piece 10's record tree joins, each of which a check's failure can stand on or climb to. */
export type MarkedRecordType = 'CompanyRecord' | 'SiteRecord' | 'LocationRecord' | 'EmploymentRecord' | 'PersonRecord';

/** Where a failure stands: the record that failed its check, and the name its line reads it by. */
interface FailedOn {
  recordType: MarkedRecordType;
  recordId: string;
  label: string;
}

/** 🔒 One failed check on one record (decision 10070), as the server climbs it up the record tree and the row's hover reads it. */
export type FailedCheck = (FailedOn & { check: 'w9'; gap: W9Gap }) | (FailedOn & { check: 'taxExemptCertificate'; gap: CertificateGap });

/** The Marks word each check's failure reads as, which the two built-in views and the export read. */
export const CHECK_MARK: Readonly<Record<RecordCheck, 'missingW9' | 'noTaxExemptCertificate'>> = {
  w9: 'missingW9',
  taxExemptCertificate: 'noTaxExemptCertificate',
};

/** 🔒 One line of a triangle's hover: the failing record named, and why, in the check's own sentence (decision 10070). */
export function failedCheckLine(failure: FailedCheck): string {
  return failure.check === 'w9' ? w9GapReason(failure.gap) : certificateGapReason(failure.gap, failure.label);
}

/** 🔨 The row field's lines are joined by a line break, which the triangle's title draws as one line per failing record. */
export const MARK_LINE_BREAK = '\n';

/** A row's `markReasons` as the hover's lines: one per failing record, none where it carries no failure. */
export function markReasonLines(value: unknown): string[] {
  return typeof value === 'string' ? value.split(MARK_LINE_BREAK).map((line) => line.trim()).filter(Boolean) : [];
}

/**
 * 🔒 The record page's address parameter naming the tile to bring into view (decision 9720): the Marks
 * column's triangle opens a company at its Vendor tile or its Sites tile.
 */
export const RECORD_TILE_PARAM = 'tile';

/** The tile a triangle whose first line is that mark's opens the company at: the Vendor tile for a W-9, the Sites tile for a certificate. */
export const MARK_TILE: Readonly<Record<'missingW9' | 'noTaxExemptCertificate', string>> = {
  missingW9: 'vendor',
  noTaxExemptCertificate: 'sites',
};
